import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AppSettingService, AppUtilityService } from '../app.service';
import { IOnetOccupation } from '../onet/onet.service';
import { IMediaFile } from '../media/media.service';
import { UserInfoService } from '../user/user-info.service';
import { JobPostApplicantOfferComponent } from './myJobApplication/my-job-application-offer.component';

export const JobPostJobTypes: string[] = ["Full Time", "Part Time", "Consulting", "Internship", "Apprenticeship"];
export const JobPostSeniorities: string[] = ["Apprenticeship", "Intern", "Entry", "Mid/Senior", "Director", "Upper Management"];
export const JobPostWorkLifes: string[] = ["Remote", "In Person", "Hybrid"];
export type TJobPostJobType = typeof JobPostJobTypes[number] | "";
export type TJobPostVisibility = "Public" | "Highlight";
export type TJobPostSeniority = typeof JobPostSeniorities[number] | "";
export type TJobPostWorkLife = typeof JobPostWorkLifes[number] | "";
export type TJobPostStatus = "Basics" | "Tags" | "Skills" | "Review" | "Live" | "Closed";
export type TJobPostAction = "create" | "modify" | "close" | "continue" | "delete" | "reactivate" | "share" | "applicant";
export type TJobApplicantStatus = "InReview" | "OfferSent" | "OfferDeclined" | "OfferAccept" | "Archive";

export interface IJobSkill {
	SkillId: string,
	SkillName: string,
	Type: string,
	RawSkills: string[],
	Ranking: number,
	Selected: boolean,
	Required: boolean
}

export interface IJobSkillType {
	TypeId: string,
	Type: string,
	Count: number,
	SelectedCount: number,
	JobSkills: IJobSkill[]
}

export interface IJobProfessions {
	ProfessionName: string,
	GroupName: string,
	GroupPercent: number
}

export interface IJobLandingPage {
	ZoneLandingPageId: number,
	EndpointPathSegmentName: string
}

export interface IJobPostOccupation extends IOnetOccupation {
	Description: string,
	Image: string
}

export interface IJobPost {
	JobPostId: string,
	Logo: string,
	Employer: string,
	Title: string,
	Description: string,
	JobType: TJobPostJobType,
	Salary: string,
	Experience: string,
	Visibility: TJobPostVisibility,
	Seniority: TJobPostSeniority,
	InternalHireOnly: boolean,
	StartDate: string
	City: string,
	State: string,
	ZipCode: string,
	WorkLife: TJobPostWorkLife,
	OnetCodes: IJobPostOccupation[],
	JobStatus: TJobPostStatus,
	DescriptionFile: any,
	DescriptionFileName: string,
	DescriptionFileType: string,
	DescriptionFileExt: string,
	HasParsed: boolean,
	HasShared: boolean,
	JobSkills: IJobSkill[],
	JobProfessions: IJobProfessions[],
	LandingPages: IJobLandingPage[];
	ZoneId: string,
	FileGuid: string
}

export interface IJobPostHistory {
	JobPostId: string,
	Employer: string,
	Title: string,
	JobStatus: TJobPostStatus,
	JobType: string,
	Seniority: string,
	InternalHireOnly: boolean,
	FirstOnetCode: string,
	HasParsed: boolean,
	HasShared: boolean,
	EditedTime: string,
	ApplicantTotal: number,
	Logo: string,
	Location: string
}

export interface IJobPostHistoryAction {
	JobPostId: string,
	Action: TJobPostAction
}

export interface IJobPostApply {
	Job: IJobPost,
	Applicant: IJobApplicantDetail,
	DisableApply: boolean,
	SameZone: boolean,
	Status: any
}

export interface IJobApplicant {
	PersonId: string,
	FirstName: string,
	LastName: string,
	Status: TJobApplicantStatus,
	ApplyDate: string
}

export interface IJobApplicantSkill {
	SkillId: string,
	Skillame: string,
	MonthsExperience: string,
	SkillFrom: string
}

export interface IJobApplicantSkillGap {
	User: string,
	QualifiedPercent: number,
	GapPercent: number,
	Skills: {
		SkillId: string,
		SkillName: string,
		MonthsExperience: string,
		SkillFrom: string
		Gap: boolean
	}[]
}

export interface IJobApplicantScore {
	ScoreId: string
	ReviewerName: string,
	ReviewerPersonId: string,
	Score: string,
	ScoreTime : string
}

export interface IJobApplicantComment {
	CommentId: string,
	ReviewerName: string,
	ReviewerPersonId: string,
	Comment: string,
	IsPrivate: boolean,
	CommentTime: string
}

export interface IJobApplicantNewComment {
	Comment: string,
	IsPrivate: boolean
}

export interface IJobApplicantReview {
	Status: string,
	Score: string
}

export interface IJobApplicantSendOffer {
	Message: string,
	Email: string,
	Phone: string,
	File: IMediaFile
}

export interface IJobApplicantOffer {
	OfferId: string,
	JobTitle: string,
	JobStatus: TJobPostStatus,
	ReviewerName: string,
	ReviewerPersonId: string,
	Message: string,
	Filename: string
	Email: string,
	Phone: string,
	OfferTime: string,
	AcceptOffer?: boolean,
	AcceptTime: string,
	OfferStatus: string
}

export interface IJobApplicantDetail {
	PersonId: string,
	FirstName: string,
	LastName: string,
	ApplyDate: string,
	ResumeName: string,
	MatchPercent: number,
	Status: TJobApplicantStatus,
	Scores: IJobApplicantScore[],
	ResumeSkills: IJobApplicantSkill[],
	JobSkills: IJobSkill[],
}

export interface IJobShareUser {
	PersonId: string,
	FirstName: string,
	LastName: string,
	EmailUsername: string,
	IsSelected: boolean,
	IsUpdating: boolean,
	isChecked: boolean
}

export interface IJobPostShareUserApply {
	Filters: string[],
	Keyword: string,
	CheckAll: boolean,
	IncludedUser: number[],
	ExcludedUsers: number[]
}

export interface IJobPostMyApplication {
	JobPostId: string,
	Title: string,
	MatchPercent: number,
	Status: TJobApplicantStatus,
	AcceptOffer?: boolean,
	OfferTime: string,
	ApplyDate: string
}
//#endregion job post objects


@Injectable({
	providedIn: 'root'
})
export class JobPostAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }
	private postUrl = AppSettingService.appConfig.JourneyAPIUrl + "JobPost";

	postJob(job: IJobPost): Rx.Observable<IJobPost> {
		let formData: FormData = new FormData();
		AppUtilityService.objectToFormData(job, formData, null);
		if (job.DescriptionFile != null) {
			formData.append('DescriptionFile', job.DescriptionFile, job.DescriptionFileName);
		}
		return this.http.post<any>(
			this.postUrl,
			formData,
			{
				withCredentials: true,
				headers: {
					'Accept': 'application/json',
					'Authorization': this.userInfo.authHeader(false)
				}
			}
		);
	}

	saveJobAttribute(jobPostId: string, job: IJobPost): Rx.Observable<IJobPost> {
		let formData: FormData = new FormData();
		AppUtilityService.objectToFormData(job, formData, null);
		return this.http.patch<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Attribute",
			formData,
			{
				withCredentials: true,
				headers: {
					'Accept': 'application/json',
					'Authorization': this.userInfo.authHeader(false)
				}
			}
		);
	}

	updateJobSkills(jobPostId: string, skills: IJobSkill[]): Rx.Observable<IJobSkill[]> {
		return this.http.patch<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Skill",
			skills,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateJobStatus(jobPostId: string, status: TJobPostStatus): Rx.Observable<any> {
		return this.http.patch<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Status/" + encodeURIComponent(status),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getPost(jobPostId: string): Rx.Observable<IJobPost> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	downloadPostDocument(jobPostId: string): Rx.Observable<Blob> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Document",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) },
				responseType: 'blob' as 'json'
			}
		);
	}

	downloadPostDescription(jobPostId: string): Rx.Observable<Blob> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Description",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) },
				responseType: 'blob' as 'json'
			}
		);
	}

	getPosts(
		keyword: string,
		jobType: TJobPostJobType,
		internalOnly: boolean,
		seniority: TJobPostSeniority,
		landingPage: string,
		sortColumn: "Title" | "JobStatus" | "EditedTime" | "JobType" | "Applications" | "",
		sortOrder: "ASC" | "DESC" | "",
		limit: number,
		offset: number
	): Rx.Observable<IJobPostHistory[]> {
		return this.http.get<any>(
			this.postUrl
			+ "?keyword=" + encodeURIComponent(keyword)
			+ "&jobType=" + encodeURIComponent(jobType)
			+ (internalOnly === null ? "" : "&internalOnly=" + encodeURIComponent(internalOnly))
			+ "&seniority=" + encodeURIComponent(seniority)
			+ "&landingPage=" + encodeURIComponent(landingPage)
			+ "&sortColumn=" + encodeURIComponent(sortColumn)
			+ "&sortOrder=" + encodeURIComponent(sortOrder)
			+ "&limit=" + limit.toString()
			+ "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deletePost(jobPostId: string): Rx.Observable<IJobPost[]> {
		return this.http.delete<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion job post

	//#region share user
	isPostShareUserInProcess(jobPostId: string): Rx.Observable<boolean> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/User/InProcess",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(map(
			response => {
				return response.Detail == "true"? true: false;
			}
		));
	}

	applyPostShareUsers(jobPostId: string, apply: IJobPostShareUserApply): Rx.Observable<any> {
		return this.http.post<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/Users",
			apply,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	searchPostShareUsers(jobPostId: string, filters: string[], keyword: string, limit: number, offset: number, sortOrder: "ASC" | "DESC"): Rx.Observable<IJobShareUser[]> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/User/Search?filters=" + encodeURIComponent(filters.join(","))  + "&keyword=" + encodeURIComponent(keyword) + "&limit=" + limit.toString() + "&offset=" + offset.toString() + "&sortOrder=" + sortOrder,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	searchPostShareUserTotal(jobPostId: string, filters: string[], keyword: string): Rx.Observable<number> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/User/Search/Total?filters=" + encodeURIComponent(filters.join(",")) + "&keyword=" + encodeURIComponent(keyword),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(map(
			response => {
				return parseInt(response.Detail);
			}
		));
	}
	
	deletePostShareUser(jobPostId: string, personId: string): Rx.Observable<any> {
		return this.http.delete<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/User/" + + encodeURIComponent(personId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	addPostShareUser(jobPostId: string, personId: string): Rx.Observable<any> {
		return this.http.patch<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/User/" + + encodeURIComponent(personId),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getPostShareUsers(jobPostId: string, limit:number, offset: number): Rx.Observable<IJobShareUser[]> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/User?limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getPostShareUserTotal(jobPostId: string): Rx.Observable<number> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/User/Total",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(map(
			response => {
				return parseInt(response.Detail);
			}
		));
	}

	savePostShareEmail(jobPostId: string, emails: string[], message: string): Rx.Observable<any> {
		return this.http.post<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Share/Email",
			{
				Emails: emails,
				Message: message
			},
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	lookupEmail(kw: string, limit: number): Rx.Observable<string[]> {
		return this.http.get<any>(
			this.postUrl + "/Email?kw=" + encodeURIComponent(kw) + "&limit=" + limit.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion share user

	//#region applicant
	getPostApply(jobPostId: string): Rx.Observable<IJobPostApply> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Apply",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	savePostApplicant(jobPostId: string, resumeId: string): Rx.Observable<IJobPostApply> {
		return this.http.post<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + resumeId.toString(),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getPostApplicants(jobPostId: string, requiredSkill: string[]): Rx.Observable<IJobApplicant[]> {
		let formData: FormData = new FormData();
		requiredSkill.forEach(x => formData.append("requiredSkills", x));		
		return this.http.post<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant",
			formData,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getPostApplicant(jobPostId: string, applicantPersonId: string): Rx.Observable<IJobApplicantDetail> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updatePostApplicant(jobPostId: string, applicantPersonId: string, review: IJobApplicantReview): Rx.Observable<any> {
		return this.http.patch<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId),
			review,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	savePostApplicantComment(jobPostId: string, applicantPersonId: string, comment: IJobApplicantNewComment): Rx.Observable<any> {
		return this.http.post<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId) + "/Comment",
			comment,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getPostApplicantComments(jobPostId: string, applicantPersonId: string, sortBy: "time"|"reviewer", orderBy: "desc"|"asc"): Rx.Observable<IJobApplicantComment[]> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId) + "/Comment?sortBy=" + encodeURIComponent(sortBy) + "&orderBy=" + encodeURIComponent(orderBy),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deletePostApplicantComment(jobPostId: string, applicantPersonId: string, commentId: string): Rx.Observable<any> {
		return this.http.delete<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId) + "/Comment/" + encodeURIComponent(commentId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveOffer(jobPostId: string, applicantPersonId: string, offer: IJobApplicantSendOffer): Rx.Observable<any> {
		let formData: FormData = new FormData();
		AppUtilityService.objectToFormData(offer, formData, null);
		if (offer.File != null) {
			formData.append('File', offer.File.File);
		}
		return this.http.post<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId) + "/Offer",
			formData,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getOffers(jobPostId: string, applicantPersonId: string): Rx.Observable<IJobApplicantOffer[]> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId) + "/Offer",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	downloadOfferAttachment(jobPostId: string, applicantPersonId: string, offerId: string): Rx.Observable<Blob> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId) + "/Offer/" + encodeURIComponent(offerId) + "/Attachment",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) },
				responseType: 'blob' as 'json'
			}
		);
	}

	deleteOffer(jobPostId: string, applicantPersonId: string, offerId: string): Rx.Observable<any> {
		return this.http.delete<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId) + "/Offer/" + encodeURIComponent(offerId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deletePostApplicant(jobPostId: string, applicantPersonId: string): Rx.Observable<IJobApplicant[]> {
		return this.http.delete<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	downloadPostApplicantResume(jobPostId: string, applicantPersonId: string): Rx.Observable<Blob> {
		return this.http.get<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant/" + encodeURIComponent(applicantPersonId) + "/Resume",
			{
				responseType: 'blob' as 'json',
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	rankPostApplicants(jobPostId: string, personIds: string[]): Rx.Observable<any> {
		return this.http.patch<any>(
			this.postUrl + "/" + encodeURIComponent(jobPostId) + "/Applicant",
			personIds,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	populateSkillGap(jobSkills: IJobSkill[], userSkills: IJobApplicantSkill[]): IJobApplicantSkillGap {
		if (jobSkills == null || jobSkills.length == 0) {
			return null;
		}
		let applicantGap: IJobApplicantSkillGap = {
			User: "",
			QualifiedPercent: 0,
			GapPercent: 0,
			Skills: []
		};

		jobSkills.forEach(x => {
			if (x.Selected) {
				let skill = userSkills.find(y => y.SkillId === x.SkillId);
				applicantGap.Skills.push(
					{
						SkillId: x.SkillId,
						SkillName: x.SkillName,
						MonthsExperience: (skill == undefined ? "" : skill.MonthsExperience),
						SkillFrom: (skill == undefined ? "" : skill.SkillFrom),
						Gap: (skill == undefined ? true : false)
					}
				)
			}
		});
		if (applicantGap.Skills.length > 0) {
			applicantGap.QualifiedPercent = Math.round((applicantGap.Skills.filter(x => x.Gap == false).length / applicantGap.Skills.length * 100));
			applicantGap.GapPercent = 100 - applicantGap.QualifiedPercent;
		}
		return applicantGap;
	}

	getMyApplications(): Rx.Observable<IJobPostMyApplication[]> {
		return this.http.get<IJobPostMyApplication[]>(
			this.postUrl + "/MyApplication",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getLastOffer(jobPostId: string): Rx.Observable<IJobApplicantOffer> {
		return this.http.get<IJobApplicantOffer>(
			this.postUrl + "/MyApplication/" + encodeURIComponent(jobPostId) + "/Offer",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateOffer(jobPostId: string, offerId: string, acceptOffer: boolean): Rx.Observable<IJobApplicantOffer> {
		return this.http.patch<IJobApplicantOffer>(
			this.postUrl + "/MyApplication/" + encodeURIComponent(jobPostId) + "/Offer/" + encodeURIComponent(offerId) + "/" + acceptOffer.toString(),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}


@Injectable({
	providedIn: 'root'
})
export class JobPostService {
	constructor(
		private dialog: MatDialog
	) { }

	viewOffer(jobPostId: string): Rx.Observable<any> {
		let popup = this.dialog.open(
			JobPostApplicantOfferComponent,
			{
				width: "600px",
				panelClass: 'jmDialogNoScroll',
				disableClose: true,
				data: {
					jobPostId: jobPostId
				}
			}
		);
		return popup.afterClosed();
	}
}
